import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { listFarmerProfiles, createFarmerProfile, deleteFarmerProfile } from "@/lib/api";
import { Plus, X, Search, Eye, Trash2, Users } from "lucide-react";

const TALUKAS = ["Jalna", "Badnapur", "Ambad", "Ghansawangi", "Partur", "Mantha", "Bhokardan", "Jaffrabad", "Other"];

export default function AdminFarmerProfiles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ q: "", village: "", taluka: "", fpo: "" });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", alternate_phone: "", village: "",
    taluka: "Jalna", district: "Jalna", state: "Maharashtra", fpo_name: "", notes: "",
  });

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    listFarmerProfiles(params).then(setItems).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };
  const onClear = () => { setFilters({ q: "", village: "", taluka: "", fpo: "" }); setTimeout(load, 0); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.village) {
      toast.error(t("toast.fill_required"));
      return;
    }
    try {
      const created = await createFarmerProfile(form);
      toast.success(`Created ${created.farmer_code}`);
      setShowForm(false);
      setForm({ full_name: "", phone: "", alternate_phone: "", village: "", taluka: "Jalna", district: "Jalna", state: "Maharashtra", fpo_name: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.failed"));
    }
  };

  const onDelete = async (id, code) => {
    if (!window.confirm(`Delete ${code}? This also removes linked orchards.`)) return;
    await deleteFarmerProfile(id);
    toast.success(t("toast.deleted"));
    load();
  };

  return (
    <div data-testid="admin-farmer-profiles">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[var(--tr-deep-green)]" />
          <h2 className="tr-section-title text-3xl">Farmer Profiles</h2>
        </div>
        <button data-testid="admin-add-farmer-profile" onClick={() => setShowForm((s) => !s)} className="tr-pill-btn tr-btn-primary">
          {showForm ? <><X className="w-4 h-4" /> {t("common.cancel")}</> : <><Plus className="w-4 h-4" /> Add farmer</>}
        </button>
      </div>

      {showForm && (
        <form data-testid="admin-farmer-profile-form" onSubmit={submit} className="tr-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name*"><input required data-testid="fp-full-name" className="tr-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
          <Field label="Mobile*"><input required inputMode="tel" data-testid="fp-phone" className="tr-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Alternate mobile"><input inputMode="tel" data-testid="fp-alt-phone" className="tr-input" value={form.alternate_phone} onChange={(e) => setForm({ ...form, alternate_phone: e.target.value })} /></Field>
          <Field label="Village*"><input required data-testid="fp-village" className="tr-input" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} /></Field>
          <Field label="Taluka">
            <select data-testid="fp-taluka" className="tr-input" value={form.taluka} onChange={(e) => setForm({ ...form, taluka: e.target.value })}>
              {TALUKAS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="District"><input data-testid="fp-district" className="tr-input" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></Field>
          <Field label="State"><input data-testid="fp-state" className="tr-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          <Field label="FPO name"><input data-testid="fp-fpo" className="tr-input" value={form.fpo_name} onChange={(e) => setForm({ ...form, fpo_name: e.target.value })} /></Field>
          <Field label="Notes" className="md:col-span-2"><textarea data-testid="fp-notes" className="tr-input tr-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="md:col-span-2">
            <button data-testid="admin-farmer-profile-submit" type="submit" className="tr-pill-btn tr-btn-zest">Create farmer</button>
          </div>
        </form>
      )}

      {/* Filters */}
      <form onSubmit={onSearch} className="tr-card p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <label className="block md:col-span-2">
          <span className="tr-label">Search (name / phone / code)</span>
          <input data-testid="fp-filter-q" className="tr-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </label>
        <label className="block">
          <span className="tr-label">Village</span>
          <input data-testid="fp-filter-village" className="tr-input" value={filters.village} onChange={(e) => setFilters({ ...filters, village: e.target.value })} />
        </label>
        <label className="block">
          <span className="tr-label">Taluka</span>
          <input data-testid="fp-filter-taluka" className="tr-input" value={filters.taluka} onChange={(e) => setFilters({ ...filters, taluka: e.target.value })} />
        </label>
        <label className="block">
          <span className="tr-label">FPO</span>
          <input data-testid="fp-filter-fpo" className="tr-input" value={filters.fpo} onChange={(e) => setFilters({ ...filters, fpo: e.target.value })} />
        </label>
        <div className="md:col-span-5 flex gap-3">
          <button type="submit" data-testid="fp-filter-apply" className="tr-pill-btn tr-btn-primary !min-h-[44px] !py-2 !px-4"><Search className="w-4 h-4" /> Apply filters</button>
          <button type="button" data-testid="fp-filter-clear" onClick={onClear} className="tr-pill-btn tr-btn-outline !min-h-[44px] !py-2 !px-4">Clear</button>
          <div className="ml-auto text-sm text-[var(--tr-muted)] self-center">
            {items ? `${items.length} record${items.length === 1 ? "" : "s"}` : ""}
          </div>
        </div>
      </form>

      {!items ? (
        <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div data-testid="admin-farmer-profiles-empty" className="tr-card p-10 text-center text-[var(--tr-muted)]">
          No farmers match these filters yet. Try adjusting the search or importing a CSV.
        </div>
      ) : (
        <div data-testid="admin-farmer-profiles-table" className="tr-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tr-sage)]/50">
                <tr>
                  {["Farmer ID", "Name", "Mobile", "Village", "Taluka", "FPO", "Source"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>
                  ))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id} data-testid={`fp-row-${f.farmer_code}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                    <td className="px-4 py-3 font-mono text-[var(--tr-deep-green)] font-semibold">{f.farmer_code}</td>
                    <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{f.full_name}</td>
                    <td className="px-4 py-3">{f.phone}</td>
                    <td className="px-4 py-3">{f.village}</td>
                    <td className="px-4 py-3">{f.taluka || "—"}</td>
                    <td className="px-4 py-3 text-xs">{f.fpo_name || "—"}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-[var(--tr-sage)] text-[var(--tr-deep-green)] text-[10px] font-bold uppercase">{f.source}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`fp-view-${f.farmer_code}`}
                        onClick={() => navigate(`/admin/farmer-profiles/${f.id}`)}
                        className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-4 hover:underline"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        data-testid={`fp-delete-${f.farmer_code}`}
                        onClick={() => onDelete(f.id, f.farmer_code)}
                        className="inline-flex items-center gap-1 text-[var(--tr-zest)] hover:text-red-700 text-sm font-semibold"
                      >
                        <Trash2 className="w-4 h-4" /> {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="tr-label">{label}</span>{children}</label>;
}
